import { Alert, AlertTitle, Box, IconButton, Slide, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { forwardRef } from "react";

// Slide transition for better UX feedback
const SlideTransition = forwardRef(function SlideTransition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

function MaterialConfirmationModal(props) {
  const {
    closeModal,
    content,
    modalOpen,
    severity = "success",
    title,
    variant = "filled",
    autoHideDuration = 4000,
  } = props;

  const theme = useTheme();
  const { palette = {} } = theme;
  const { success = {}, mode } = palette;
  const greenColor = success?.main || "#A0C46D";
  const isDark = mode === "dark";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.2)" : "rgba(0, 0, 0, 0.08)";

  // Icon mapping for different severity levels
  const iconMap = {
    success: <CheckCircleOutlineIcon sx={{ fontSize: "1.5rem" }} />,
    error: <ErrorOutlineIcon sx={{ fontSize: "1.5rem" }} />,
    warning: <WarningAmberIcon sx={{ fontSize: "1.5rem" }} />,
    info: <InfoOutlinedIcon sx={{ fontSize: "1.5rem" }} />,
  };

  // Color mapping for better visual hierarchy
  const colorMap = {
    success: {
      background: greenColor,
      hover: "#7a9c4a",
    },
    error: {
      background: "#ef4444",
      hover: "#dc2626",
    },
    warning: {
      background: "#f59e0b",
      hover: "#d97706",
    },
    info: {
      background: "#3b82f6",
      hover: "#2563eb",
    },
  };

  const currentColor = colorMap[severity] || colorMap.success;

  // Auto-close after duration (UX principle: don't require user action for success messages)
  if (modalOpen && severity === "success" && autoHideDuration > 0) {
    setTimeout(() => {
      closeModal();
    }, autoHideDuration);
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      onClose={() => {
        closeModal();
      }}
      open={modalOpen || false}
      TransitionComponent={SlideTransition}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? "rgba(0, 0, 0, 0.4)" : "rgba(30, 41, 59, 0.35)",
          backdropFilter: "blur(6px)",
        },
      }}
      PaperProps={{
        sx: {
          position: "fixed",
          top: "24px",
          left: "50%",
          transform: "translateX(-50%)",
          margin: 0,
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: isDark ? "0 12px 48px rgba(0, 0, 0, 0.35)" : "0 12px 48px rgba(0, 0, 0, 0.12)",
          border: `1px solid ${borderColor}`,
        },
      }}
      // Accessibility: Allow closing with Escape key
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
    >
      <Alert
        icon={iconMap[severity]}
        action={
          <IconButton
            aria-label="Close notification"
            color="inherit"
            size="small"
            onClick={() => {
              closeModal();
            }}
            sx={{
              padding: "8px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
              "&:focus-visible": {
                outline: "2px solid white",
                outlineOffset: "2px",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        severity={severity}
        variant={variant}
        sx={{
          backgroundColor: currentColor.background,
          color: "white",
          padding: "16px 20px",
          alignItems: "center",
          "& .MuiAlert-icon": {
            color: "white",
            marginRight: "12px",
            padding: 0,
          },
          "& .MuiAlert-message": {
            padding: 0,
            fontSize: "0.9375rem",
            fontWeight: 500,
            fontFamily: '"Lato", sans-serif',
          },
          "& .MuiAlert-action": {
            padding: 0,
            marginRight: 0,
          },
        }}
      >
        {title ? (
          <AlertTitle
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              marginBottom: "4px",
            }}
          >
            {title}
          </AlertTitle>
        ) : null}
        <Box id="confirmation-dialog-description">{content}</Box>
      </Alert>

      {/* Progress bar for auto-close feedback (UX: show time remaining) */}
      {severity === "success" && autoHideDuration > 0 && (
        <Box
          sx={{
            height: "3px",
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              backgroundColor: "white",
              animation: `shrink ${autoHideDuration}ms linear forwards`,
              "@keyframes shrink": {
                "0%": { width: "100%" },
                "100%": { width: "0%" },
              },
            }}
          />
        </Box>
      )}
    </Dialog>
  );
}

export default MaterialConfirmationModal;
